import { useMutation, useQuery } from "@apollo/client";
import { Button, Link, Paper, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { loadStripe } from "@stripe/stripe-js";
import {
    GRAPHQL_MUTATION_CANCEL_MEMBERSHIP,
    GRAPHQL_MUTATION_MEMBERSHIP_PAYMENT_METHOD,
    GRAPHQL_QUERY_NEXT_INVOICE,
} from "app/queries";
import { StripePublicKey } from "app/stripe";
import { LIMITED_WIDTH, LIMITED_WIDTH_LARGE } from "app/styles";
import BillingPlanExplanation from "features/billing/BillingPlanExplanation";
import { CSDialog } from "components/CSDialog";
import { NavLink } from "features/navigation/NavLink";
import LoadingPage from "features/pages/LoadingPage";
import _ from "lodash";
import { DateTime } from "luxon";
import { useContext, useState } from "react";
import * as React from "react";
import { getFullUrl, routes } from "../../../app/routes";
import { MemberContext } from "../MemberZone";
import FreeTrialMembershipExplanation from "./FreeTrialMembershipExplanation";
import { MembershipNotFound } from "./MembershipNotFound";
import { LoggedInUserProfileContext } from "features/auth/RequireAuthentication";
import { ProductFeature } from "@app/shared/types";
import { MembershipStatus } from "@app/shared/constants";
import CancelledMembershipExplanation from "./CancelledMembershipExplanation";
import { dateTimeFromString } from "@app/shared/utils";
import { useUserTimezone } from "hooks/useUserTimezone";
import { OptionBox } from "components/OptionBox";
import { useHistory } from "react-router";
import {
    isMembershipCohortSponsored,
    membershipUsesFreeTrialInternally,
} from "./membershipHelpers";

const StripeDevelopmentMode = process.env.REACT_APP_STRIPE_DEVELOPMENT_MODE === "true";

const getStripeInstance = () => {
    return loadStripe(StripePublicKey);
};

const useStyles = makeStyles((theme) => ({
    billingLink: {
        display: "inline-block",
        marginRight: theme.spacing(2),
    },

    billingPlanExplanation: {
        maxWidth: LIMITED_WIDTH_LARGE,
    },
    cancelButton: {
        display: "block",
        marginBottom: theme.spacing(2),
        textAlign: "center",
        margin: "0 auto",
    },
    modal: {
        textAlign: "center",
        maxWidth: 620,
        margin: "0 auto",
    },
    modalTitle: {
        marginTop: theme.spacing(2),
    },
    box: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(6),
        borderRadius: theme.borderRadius.default,
        backgroundColor: theme.palette.background.contrast,
        maxWidth: LIMITED_WIDTH,
    },
    optionBox: {
        margin: 0,
    },
}));

export const MyMembership = (): JSX.Element => {
    const classes = useStyles();
    const history = useHistory();
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [updateMembershipOrBillingOpen, setUpdateMembershipOrBillingOpen] = useState(false);
    const [cancellingMembership, setIsCancellingMembership] = useState(false);

    const memberContext = useContext(MemberContext);
    const activeMemberships = memberContext.memberships;
    const membership = _.first(activeMemberships);

    const user = useContext(LoggedInUserProfileContext);
    const isImmersion = user.features.includes(ProductFeature.DEDICATED_SANGHA);
    const timezone = useUserTimezone();

    const { data } = useQuery(GRAPHQL_QUERY_NEXT_INVOICE, {
        variables: {
            membershipId: membership?.id,
        },
        fetchPolicy: "network-only",
    });

    const nextInvoiceAmount = data?.nextStripeInvoice?.amountDue;

    // date is not used for now, but we keep the functionality for future use
    const nextInvoiceDate = data?.nextStripeInvoice?.invoiceDate
        ? DateTime.fromSeconds(data?.nextStripeInvoice?.invoiceDate)
        : undefined;

    // Note; If there's a scheduled change, we show the future billing choice
    const billingChoice =
        membership && (membership.scheduledChange?.billingChoice || membership.billingChoice);

    const [updatePaymentMethodMutation, { loading: isSaving }] = useMutation(
        GRAPHQL_MUTATION_MEMBERSHIP_PAYMENT_METHOD,
    );

    const [cancelMembership] = useMutation(GRAPHQL_MUTATION_CANCEL_MEMBERSHIP, {
        variables: {
            membershipId: membership?.id,
            cancelDate: DateTime.now().toISO(),
        },
    });

    const onUpdatePaymentMethod = async (ev: React.MouseEvent) => {
        ev.preventDefault();
        setUpdateMembershipOrBillingOpen(false);
        const result = await updatePaymentMethodMutation({
            variables: {
                membershipId: membership?.id,
                successUrl: getFullUrl(routes.memberMembership("success")),
                failedUrl: getFullUrl(routes.memberMembership("failed")),
                developmentMode: StripeDevelopmentMode,
            },
        });

        const sessionId = result.data.updateMembershipBillingPaymentMethod;
        const stripe = await getStripeInstance();
        stripe?.redirectToCheckout({ sessionId: sessionId });
    };

    // loading state
    if (!membership || !billingChoice) {
        return <MembershipNotFound />;
    }
    if (isSaving) {
        return <LoadingPage caption="Redirecting to payment form" />;
    }

    const { plan, donationAmount } = billingChoice;
    const showFreeTrialMessaging = membershipUsesFreeTrialInternally(membership);

    const isCancelledMembership = membership.status === MembershipStatus.PendingCancel;

    if (isCancelledMembership) {
        const endDate = isImmersion
            ? membership.sanghaMemberships[0].endDate
            : membership.renewalDate;
        return (
            <CancelledMembershipExplanation
                cancelDate={DateTime.fromISO(endDate)}
                isImmersion={isImmersion}
                classes={{
                    root: classes.billingPlanExplanation,
                }}
            />
        );
    }

    const renderFreeTrialBillingPlan = () => (
        <>
            <FreeTrialMembershipExplanation
                freeTrialEndDate={DateTime.fromISO(membership.freeTrialEndDate)}
                basePrice={plan.baseAmount}
                billingPeriod={plan.billedEvery}
                classes={{
                    root: classes.billingPlanExplanation,
                }}
            />
        </>
    );

    const isMembershipTypeScheduledToChange = () =>
        membership.scheduledChange &&
        membership.scheduledChange.billingChoice.plan.membershipType !==
            membership.billingChoice.plan.membershipType;

    const renderMembershipTypeUpdateExplanation = () => {
        if (!membership.scheduledChange) {
            return null;
        }

        const futurePlan = membership.scheduledChange.billingChoice.plan;
        const changeDate = dateTimeFromString(
            membership.scheduledChange.changeDate,
            timezone,
        ).toLocaleString(DateTime.DATE_FULL);

        return (
            <Paper elevation={0} className={classes.box}>
                <Typography variant="body2">
                    Your membership will change to{" "}
                    {futurePlan.extendedDisplayName || futurePlan.displayName} on {changeDate}.
                    Please <NavLink to={routes.contactUs()}>contact support</NavLink> if you'd like
                    to further update your membership before then.
                </Typography>
            </Paper>
        );
    };

    const renderMultipleMembershipsExplanation = () => {
        return (
            <Paper elevation={0} className={classes.box}>
                <Typography variant="body2">
                    You have multiple active memberships - please{" "}
                    <NavLink to={routes.contactUs()}>contact us</NavLink> for more information.
                </Typography>
            </Paper>
        );
    };

    const renderBillingPlanDetails = () => {
        if (showFreeTrialMessaging) {
            return renderFreeTrialBillingPlan();
        } else if (isMembershipTypeScheduledToChange()) {
            return renderMembershipTypeUpdateExplanation();
        } else {
            return (
                <BillingPlanExplanation
                    plan={plan}
                    donationPrice={donationAmount}
                    renewalDate={DateTime.fromISO(membership.renewalDate)}
                    nextInvoiceAmount={nextInvoiceAmount}
                    classes={{
                        root: classes.billingPlanExplanation,
                    }}
                />
            );
        }
    };

    const shouldShowUpdateMembership = () =>
        !showFreeTrialMessaging && !isMembershipTypeScheduledToChange();

    const renderUpdateMembershipOrBillingDialog = () => (
        <CSDialog
            onClose={() => {
                setUpdateMembershipOrBillingOpen(false);
            }}
            open={updateMembershipOrBillingOpen}
            className={classes.modal}
        >
            <Typography variant="h4">What change would you like to make?</Typography>

            {shouldShowUpdateMembership() && (
                <OptionBox onClick={() => history.push(routes.memberUpdateBilling())}>
                    <Typography variant="body1" className={classes.optionBox}>
                        Adjust my contribution
                    </Typography>
                </OptionBox>
            )}

            {!isMembershipCohortSponsored(membership) && (
                <OptionBox onClick={onUpdatePaymentMethod}>
                    <Typography variant="body1" className={classes.optionBox}>
                        Update my card
                    </Typography>
                </OptionBox>
            )}

            {/* <OptionBox onClick={() => history.push(routes.cancelMembership())}>
                <Typography variant="body2">Cancel my mentorship group</Typography>
            </OptionBox> */}

            <OptionBox onClick={() => history.push(routes.cancelMembershipPage1())}>
                <Typography variant="body1" className={classes.optionBox}>
                    Cancel my entire membership
                </Typography>
            </OptionBox>
        </CSDialog>
    );
    const renderUpdateMembershipOrBillingLink = () => (
        <Button
            variant="tertiary"
            className={classes.billingLink}
            onClick={(event: React.MouseEvent) => {
                event.preventDefault();
                setUpdateMembershipOrBillingOpen(true);
            }}
        >
            Update membership or billing
        </Button>
    );

    if (activeMemberships.length > 1) {
        return renderMultipleMembershipsExplanation();
    }

    return (
        <>
            {renderBillingPlanDetails()}
            {renderUpdateMembershipOrBillingDialog()}
            {renderUpdateMembershipOrBillingLink()}
        </>
    );
};
